import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Save, FileText } from "lucide-react";

const emptyForm = () => ({
  name: "", header_text: "", grant_reference: "",
  signatory_1_name: "", signatory_1_designation: "",
  signatory_2_name: "", signatory_2_designation: "",
  certification_paragraph: "",
});

export default function UCTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => api.get("/v2/uc-templates").then(r => {
    setTemplates(r.data);
    if (r.data[0] && !selected) { setSelected(r.data[0].id); setForm({ ...emptyForm(), ...r.data[0] }); }
  });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return;
    const t = templates.find(x => x.id === selected);
    if (t) setForm({ ...emptyForm(), ...t });
  }, [selected]); // eslint-disable-line

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/v2/uc-templates/${selected}`, form);
      toast.success("Template updated");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/v2/uc-templates", form);
      toast.success("Template created");
      setCreateOpen(false);
      setSelected(r.data.id);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  if (!isAdmin) return <div className="p-8 text-sm text-slate-500">Only Super Admin can edit UC templates.</div>;

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Administration · UC Module</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">UC Templates</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage donor-specific UC templates. Placeholders: <code className="bg-slate-100 px-1 rounded">{"{{amount}}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{{period}}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{{project}}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{{project_code}}"}</code>, <code className="bg-slate-100 px-1 rounded">{"{{grant_reference}}"}</code>.
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm()); setCreateOpen(true); }} className="bg-[#0F172A]" data-testid="uctpl-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader className="py-3 border-b bg-slate-50"><CardTitle className="text-sm">Templates ({templates.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {templates.map(t => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className={`w-full text-left px-4 py-3 border-b flex items-center gap-2 hover:bg-slate-50 ${selected === t.id ? "bg-slate-100" : ""}`}
                data-testid={`uctpl-item-${t.id.slice(0, 8)}`}
              >
                <FileText className="w-4 h-4 text-slate-500" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{t.name || "(untitled)"}</div>
                  <div className="text-[10px] text-slate-500">{t.id.slice(0, 8)}…</div>
                </div>
                {t.is_default && <Badge variant="outline" className="text-[9px]">default</Badge>}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="py-3 border-b bg-slate-50 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">Edit Template</CardTitle>
            <Button onClick={save} disabled={!selected || busy} size="sm" className="bg-[#0F172A]" data-testid="uctpl-save-btn">
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </CardHeader>
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Template Name</Label>
              <Input value={form.name || ""} onChange={e => update("name", e.target.value)} data-testid="uctpl-name" />
            </div>
            <div className="md:col-span-2">
              <Label>Header Text (one line per row; will render as title)</Label>
              <Textarea value={form.header_text || ""} onChange={e => update("header_text", e.target.value)} rows={3} data-testid="uctpl-header" />
            </div>
            <div className="md:col-span-2">
              <Label>Grant Reference line</Label>
              <Input value={form.grant_reference || ""} onChange={e => update("grant_reference", e.target.value)} placeholder="Grant Ref: XX-YYYY / Sanction No. …" data-testid="uctpl-grantref" />
            </div>
            <div>
              <Label>Signatory 1 — Name</Label>
              <Input value={form.signatory_1_name || ""} onChange={e => update("signatory_1_name", e.target.value)} data-testid="uctpl-sig1-name" />
            </div>
            <div>
              <Label>Signatory 1 — Designation</Label>
              <Input value={form.signatory_1_designation || ""} onChange={e => update("signatory_1_designation", e.target.value)} data-testid="uctpl-sig1-desig" />
            </div>
            <div>
              <Label>Signatory 2 — Name</Label>
              <Input value={form.signatory_2_name || ""} onChange={e => update("signatory_2_name", e.target.value)} data-testid="uctpl-sig2-name" />
            </div>
            <div>
              <Label>Signatory 2 — Designation</Label>
              <Input value={form.signatory_2_designation || ""} onChange={e => update("signatory_2_designation", e.target.value)} data-testid="uctpl-sig2-desig" />
            </div>
            <div className="md:col-span-2">
              <Label>Certification Paragraph</Label>
              <Textarea rows={5} value={form.certification_paragraph || ""} onChange={e => update("certification_paragraph", e.target.value)}
                placeholder="Certified that the expenditure of ₹{{amount}} has been utilised for the period {{period}}…"
                data-testid="uctpl-certpara" />
              <div className="text-[10px] text-slate-500 mt-1">Supports the placeholders listed above. They are substituted at export time.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New UC Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name || ""} onChange={e => update("name", e.target.value)} placeholder="e.g. Bill & Melinda Gates Foundation" data-testid="uctpl-new-name" /></div>
            <div><Label>Header Text</Label><Textarea rows={2} value={form.header_text || ""} onChange={e => update("header_text", e.target.value)} /></div>
            <div><Label>Certification Paragraph</Label><Textarea rows={4} value={form.certification_paragraph || ""} onChange={e => update("certification_paragraph", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-[#0F172A]" data-testid="uctpl-create-confirm">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
